/* tslint:disable: max-classes-per-file */
import { DisplaySettingsState } from "@mksap/reducers/displaySettings";
import { SyncState } from "../reducers/sync";
import { ResponsesState } from "../reducers/responses/responses";
import { QuizzesState } from "../reducers/quizzes/quizzes";
import { NotesState } from "../reducers/notes";
import { QuestionStarsState } from "../reducers/questionStars/questionStars";
import { FlashcardsLocalStorage } from "../types/flashcards";
import { LivePeerComparisonState } from "../reducers/livePeerComparison/livePeerComparison";
import { HighlightingState } from "@mksap/reducers/highlighting/highlighting";
import { DismissibleAlertsState } from "@mksap/reducers/dismissibleAlerts/dismissibleAlerts";
import { ChapterReadStatusesState } from "@mksap/reducers/chapterReadStatuses/chapterReadStatuses";
import { MasteredSectionState } from "@mksap/reducers/masteredSections/masteredSections";
import localForage from "localforage";
import { LearningPlanState } from "@mksap/reducers/learningPlan/learningPlan";
import { RandomQuestionsState } from "@mksap/reducers/randomQuestions/randomQuestions";
import { ImIteState } from "@mksap/reducers/imIte/imIte";
import { EditorialToolsState } from "@mksap/reducers/editorialTools/editorialTools";
import { appOptions } from "@mksap/appOptions";
import { RecentActivityWithMetadata } from "@mksap/types/recentActivity";
import { LearningPlanTaskStatus } from "@mksap/types/learningPlan";
import { SubmissionsState } from "@mksap/types/submissions";
import { COREState } from "@mksap/reducers/core/core";
import { AbimImportState } from "@mksap/reducers/abimImport/abimImport";

export interface UserLocalStorageData {
	displaySettings?: DisplaySettingsState;
	responses?: ResponsesState;
	sync?: SyncState;
	quizzes?: QuizzesState;
	notes?: NotesState;
	questionStars?: QuestionStarsState;
	flashcards?: FlashcardsLocalStorage;
	livePeerComparison?: LivePeerComparisonState;
	highlighting?: HighlightingState;
	dismissibleAlerts?: DismissibleAlertsState;
	learningPlan?: LearningPlanState;
	chapterReadStatuses?: ChapterReadStatusesState;
	masteredSections?: MasteredSectionState;
	randomQuestions?: RandomQuestionsState;
	imIteResults?: ImIteState;
	editorialTools?: EditorialToolsState;
	recentActivity?: RecentActivityWithMetadata[];
	learningPlanTaskStatuses?: LearningPlanTaskStatus[];
	submissions?: SubmissionsState;
	coreState?: COREState;
	abimImport?: AbimImportState | null;
}

export interface IUserLocalStorage {
	getData(): Promise<UserLocalStorageData | null>;
	setData(data: UserLocalStorageData): Promise<unknown>;
	resetData(): Promise<void>;
}

export class LocalForageUserStorage implements IUserLocalStorage {
	private localStorageKey: string;
	private data?: UserLocalStorageData | null;

	constructor(userId: string) {
		const syncEnabledSuffix = appOptions.syncEnabled ? "_sync_enabled" : "";
		this.localStorageKey = `USL_${userId}${syncEnabledSuffix}`;
	}

	async getData() {
		// Don't reload the data if it has already been loaded
		if (typeof this.data !== "undefined") {
			return this.data;
		}
		this.data = await localForage.getItem(this.localStorageKey);

		return this.data;
	}

	async setData(data: UserLocalStorageData) {
		await localForage.setItem(this.localStorageKey, data);
	}

	async resetData() {
		await localForage.removeItem(this.localStorageKey);
		this.data = null;
	}
}

// Exporting for test purposes
async function userLocalStorageFactory(
	acpNumber: string,
): Promise<IUserLocalStorage> {
	return new LocalForageUserStorage(acpNumber);
}

const userLocalStorages: Record<string, IUserLocalStorage> = {};

export async function getUserLocalStorage(
	acpNumber: string,
): Promise<IUserLocalStorage> {
	if (!userLocalStorages[acpNumber]) {
		userLocalStorages[acpNumber] = await userLocalStorageFactory(acpNumber);
	}
	return userLocalStorages[acpNumber];
}
